<template>
  <div class="relative">
    <button
      type="button"
      @click.stop="toggle"
      class="flex-shrink-0 inline-flex items-center gap-1 pl-2 pr-1 py-1.5 sm:py-1 rounded-md border text-[13px] sm:text-xs font-medium transition-colors"
      :class="[
        open
          ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
          : 'border-transparent sm:hover:border-gray-200 dark:sm:hover:border-gray-700 sm:hover:bg-gray-50 dark:sm:hover:bg-gray-800',
        overdue ? 'text-red-500' : dueAt ? 'text-gray-400 dark:text-gray-500' : 'text-gray-300 dark:text-gray-600',
      ]"
    >
      <span class="whitespace-nowrap">{{ overdue ? 'Overdue' : (dueLabel || 'No date') }}</span>
      <svg class="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    <div v-if="open" @click="close" class="fixed inset-0 z-20"></div>
    <Transition name="row-dropdown">
      <div v-if="open" @click.stop class="absolute right-0 top-full mt-1.5 w-64 sm:w-60 bg-white dark:bg-[#2A3037] border border-gray-100 dark:border-gray-700 rounded-lg shadow-lg z-30 overflow-hidden">
        <template v-if="mode === 'list'">
          <div class="py-1">
            <button
              v-for="opt in quickOptions" :key="opt.key"
              type="button"
              @click="chooseQuick(opt)"
              class="w-full flex items-center justify-between gap-2 px-3 py-2.5 sm:py-1.5 text-[13px] sm:text-xs font-medium text-left text-gray-700 dark:text-gray-200 sm:hover:bg-gray-50 dark:sm:hover:bg-gray-800 transition-colors"
            >
              {{ opt.label }}
              <span class="text-[11px] text-gray-400 dark:text-gray-500">{{ opt.hint }}</span>
            </button>
          </div>
          <div class="border-t border-gray-100 dark:border-gray-700 py-1">
            <button
              type="button"
              @click="mode = 'calendar'"
              class="w-full flex items-center gap-2 px-3 py-2.5 sm:py-1.5 text-[13px] sm:text-xs font-medium text-left text-gray-700 dark:text-gray-200 sm:hover:bg-gray-50 dark:sm:hover:bg-gray-800 transition-colors"
            >
              <svg class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"/>
              </svg>
              Custom date&hellip;
            </button>
            <button
              v-if="dueAt"
              type="button"
              @click="chooseQuick({ value: null })"
              class="w-full flex items-center gap-2 px-3 py-2.5 sm:py-1.5 text-[13px] sm:text-xs font-medium text-left text-red-500 sm:hover:bg-red-50 dark:sm:hover:bg-red-900/20 transition-colors"
            >
              <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
              Remove due date
            </button>
          </div>
        </template>

        <template v-else>
          <div class="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <button
              type="button"
              @click="mode = 'list'"
              class="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Back"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            <span class="text-xs font-semibold text-gray-600 dark:text-gray-300">Pick a date</span>
          </div>
          <v-date-picker
            v-model="calendarValue"
            mode="date"
            :is-dark="isDark"
            :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }"
            class="task-due-calendar"
          />
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useTaskDates } from 'composables/useTaskDates.js'

const props = defineProps({
  dueAt: { type: String, default: null },
  overdue: { type: Boolean, default: false },
})
const emit = defineEmits(['update'])

const { formatDueDateLabel, todayStrInUserTz, addDaysToDateStr, weekdayShort } = useTaskDates()

const dueLabel = computed(() => formatDueDateLabel(props.dueAt))
const isDark = document.documentElement.classList.contains('dark')

const open = ref(false)
const mode = ref('list')
const calendarValue = ref(null)

function toggle() {
  open.value = !open.value
  if (open.value) {
    mode.value = 'list'
    calendarValue.value = null
  }
}

function close() {
  open.value = false
}

const quickOptions = computed(() => {
  const today = todayStrInUserTz()
  const tomorrow = addDaysToDateStr(today, 1)
  const nextWeek = addDaysToDateStr(today, 7)
  return [
    { key: 'today', label: 'Today', hint: weekdayShort(today), value: today },
    { key: 'tomorrow', label: 'Tomorrow', hint: weekdayShort(tomorrow), value: tomorrow },
    { key: 'next_week', label: 'Next week', hint: weekdayShort(nextWeek), value: nextWeek },
  ]
})

// Due dates are date-only in this app's UI — always saved at end-of-day,
// same convention TaskDetailPage.vue's endOfDay() uses.
function chooseQuick(opt) {
  open.value = false
  emit('update', opt.value ? `${opt.value}T23:59:00` : null)
}

watch(calendarValue, (val) => {
  if (!val) return
  open.value = false
  emit('update', `${val}T23:59:00`)
})
</script>

<style scoped>
.row-dropdown-enter-active {
  transition: opacity 0.12s ease, transform 0.14s cubic-bezier(0.2, 0, 0.2, 1);
}
.row-dropdown-enter-from {
  opacity: 0;
  transform: translateY(-4px) scale(0.97);
}
.row-dropdown-leave-active {
  transition: opacity 0.1s ease;
}
.row-dropdown-leave-to {
  opacity: 0;
}

.task-due-calendar :deep(.vc-title) {
  font-size: 12px;
  font-weight: 600;
}
.task-due-calendar :deep(.vc-weekday) {
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
}
.task-due-calendar :deep(.vc-day-content) {
  font-size: 11px;
}
.task-due-calendar :deep(.vc-day-content:hover) {
  background-color: #eff6ff;
  color: #2563eb;
}
.task-due-calendar :deep(.vc-highlight) {
  background-color: #2563eb;
}
.task-due-calendar.vc-is-dark :deep(.vc-title) {
  color: #e5e7eb;
}
.task-due-calendar.vc-is-dark :deep(.vc-weekday) {
  color: #6b7280;
}
.task-due-calendar.vc-is-dark :deep(.vc-day-content:hover) {
  background-color: #1e3a5f;
  color: #60a5fa;
}
</style>
